<template>
  <v-card flat>
    <v-card-title>
        <span class="font-weight-bold">{{ $t('MenuPainelGerente.historicoAtendimentoDetalhe') }}</span>
      </v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="12" sm="3">
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="startDate"
                label="Data início"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
                hide-details
              />
            </template>
            <v-date-picker
              v-model="startDate"
              locale="pt-BR"
              @input="startDateMenu = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" sm="3">
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="endDate"
                label="Data fim"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
                hide-details
              />
            </template>
            <v-date-picker
              v-model="endDate"
              locale="pt-BR"
              @input="endDateMenu = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" sm="4">
          <v-text-field
            v-model="phoneNumber"
            label="Telefone"
            prepend-icon="mdi-phone"
            dense
            hide-details
            clearable
          />
        </v-col>

        <v-col cols="12" sm="2" class="d-flex align-end">
          <v-btn color="primary" small @click="searchHistory" :loading="loading">
            Pesquisar
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="my-4" />

      <v-row justify="end" class="mb-2">
        <v-col cols="auto">
          <v-btn
            color="secondary"
            small
            @click="exportTableCSV"
            :disabled="!reportRows.length"
          >
            Exportar CSV
          </v-btn>
        </v-col>
      </v-row>

      <div v-if="tableHeaders.length" class="historico-data-table elevation-1">
        <v-simple-table>
          <colgroup>
            <col
              v-for="header in tableHeaders"
              :key="header.value"
              :style="headerStyle(header.value)"
            />
          </colgroup>
          <thead>
            <tr>
              <th
                v-for="header in tableHeaders"
                :key="header.value"
                :style="headerStyle(header.value)"
                class="resizable-header"
              >
                <div class="header-label">{{ header.text }}</div>
                <span
                  class="resize-handle"
                  @mousedown.prevent="startResize($event, header.value)"
                ></span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in reportRows"
              :key="index"
              :class="{ 
                'selected-row': selectedRowIndex === index,
                'row-group-0': rowColorGroups[index] === 0,
                'row-group-1': rowColorGroups[index] === 1
              }"
              @click="selectRow(index)"
            >
              <td
                v-for="header in tableHeaders"
                :key="header.value"
                :style="headerStyle(header.value)"
              >
                {{ formatCell(item, header.value) }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <div v-else class="text-center py-6">
        <span>{{ noDataText }}</span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'HistoricoMensagensAnteriores',
  data() {
    return {
      startDate: '',
      endDate: '',
      phoneNumber: '',
      startDateMenu: false,
      endDateMenu: false,
      loading: false,
      selectedRowIndex: null,
      columnWidths: {},
      resizingColumn: null,
      startX: 0,
      startWidth: 0,
      identifierField: null, // Campo usado como identificador
    };
  },
  computed: {
    ...mapGetters({
      reportRows: 'getHistoricoReportDetails',
    }),
    tableHeaders() {
      if (!Array.isArray(this.reportRows) || this.reportRows.length === 0) {
        return [];
      }
      const firstRow = this.reportRows[0];
      if (firstRow && typeof firstRow === 'object' && !Array.isArray(firstRow)) {
        return Object.keys(firstRow).map((key) => ({
          text: this.formatHeaderLabel(key),
          value: key,
        }));
      }
      return [{ text: 'Valor', value: 'Value' }];
    },
    noDataText() {
      return this.loading
        ? 'Carregando...'
        : 'Nenhum resultado encontrado. Use os filtros acima e clique em pesquisar.';
    },
    rowColorGroups() {
      if (!Array.isArray(this.reportRows) || this.reportRows.length === 0) {
        return [];
      }

      // Detecta automaticamente o campo identificador (primeiro campo)
      const firstRow = this.reportRows[0];
      const identifier = this.identifierField || (firstRow ? Object.keys(firstRow)[0] : null);

      const groups = [];
      let currentGroup = 0;
      let lastIdentifierValue = null;

      this.reportRows.forEach((row) => {
        const currentIdentifierValue = row[identifier];

        // Se o identificador mudou, alterna o grupo (0 ou 1)
        if (lastIdentifierValue !== null && currentIdentifierValue !== lastIdentifierValue) {
          currentGroup = currentGroup === 0 ? 1 : 0;
        }

        groups.push(currentGroup);
        lastIdentifierValue = currentIdentifierValue;
      });

      return groups;
    },
  },
  methods: {
    ...mapActions(['getReportHistoricoDetails']),
    setIdentifierField(fieldName) {
      // Permite customizar qual campo usar como identificador
      this.identifierField = fieldName;
    },
    formatHeaderLabel(header) {
      return header
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (value) => value.toUpperCase());
    },
    formatCell(row, header) {
      if (row && typeof row === 'object') {
        const value = row[header];
        return value !== undefined && value !== null ? value : '';
      }
      return row;
    },
    async searchHistory() {
      if (!this.startDate || !this.endDate) {
        return;
      }
      this.loading = true;
      try {
        await this.getReportHistoricoDetails({
          customerId: localStorage.getItem('customer_Id'),
          start_Date: this.startDate,
          end_Date: this.endDate,
          phone_Number: this.phoneNumber || '',
        });
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.startDate = '';
      this.endDate = '';
      this.phoneNumber = '';
    },
    selectRow(index) {
      this.selectedRowIndex = index;
    },
    exportTableCSV() {
      if (!this.reportRows || !this.reportRows.length) {
        return;
      }

      const headers = this.tableHeaders.map((h) => h.text);
      const rows = this.reportRows.map((row) =>
        this.tableHeaders
          .map((h) => {
            const cell = row[h.value] != null ? row[h.value] : '';
            return `"${String(cell).replace(/"/g, '""')}"`;
          })
          .join(';')
      );

      const csvContent = [headers.join(';'), ...rows].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const fileName = 'historico_mensagens_anteriores.csv';
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    headerStyle(value) {
      const width = this.columnWidths[value];
      return width ? { width: `${width}px`, minWidth: `${width}px` } : {};
    },
    startResize(event, column) {
      this.resizingColumn = column;
      this.startX = event.clientX;
      const currentWidth = this.columnWidths[column];
      this.startWidth = currentWidth || event.target.closest('th').offsetWidth;
      document.addEventListener('mousemove', this.onResizeMouseMove);
      document.addEventListener('mouseup', this.stopResize);
    },
    onResizeMouseMove(event) {
      if (!this.resizingColumn) {
        return;
      }
      const delta = event.clientX - this.startX;
      const newWidth = Math.max(80, this.startWidth + delta);
      this.$set(this.columnWidths, this.resizingColumn, newWidth);
    },
    stopResize() {
      document.removeEventListener('mousemove', this.onResizeMouseMove);
      document.removeEventListener('mouseup', this.stopResize);
      this.resizingColumn = null;
    },
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onResizeMouseMove);
    document.removeEventListener('mouseup', this.stopResize);
  },
};
</script>

<style scoped>
.historico-data-table {
  overflow-x: auto;
}

.selected-row {
  background-color: rgba(25, 118, 210, 0.12) !important;
}

.row-group-0 {
  background-color: #ffffff;
}

.row-group-1 {
  background-color: #f5f5f5;
}

.selected-row.row-group-0,
.selected-row.row-group-1 {
  background-color: rgba(25, 118, 210, 0.12) !important;
}

.resizable-header {
  position: relative;
  user-select: none;
}

.header-label {
  display: inline-flex;
  align-items: center;
  width: calc(100% - 10px);
}

.resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  z-index: 1;
}

.v-data-table th,
.v-data-table td {
  vertical-align: middle;
}
</style>
